/**
 * 流局判定 + 连庄/轮庄 + 结算
 *
 * 流局类型：
 *   · 荒牌流局（牌山摸完）
 *   · 九种九牌（第一巡，手牌含 9 种以上幺九，可宣言）
 *   · 四风连打（第一巡，四家打同一种风牌）
 *   · 四家立直
 *   · 三家和（一炮三响）
 *
 * 结算：
 *   · 和了：点数移动 + 供托归和牌者
 *   · 流局：听牌者向未听牌者收罚符（1000/3000）
 *   · 连庄条件：庄家和了 或 庄家听牌
 */

import { isYaochu, isHonor, indexToName } from '../tile.js';
import type { GameState, RoundInfo, RoundResult, Seat, Wind } from './state.js';
export type { RoundResult };
import { dealerSeat, nextSeat, STARTING_SCORE } from './state.js';
import { handToCounts } from './rules.js';
import { shanten } from '../shanten.js';

// ════════════════════════════════════════════════════════════════
// 流局判定
// ════════════════════════════════════════════════════════════════

/**
 * 九种九牌检查（第一巡，可宣言流局）
 *
 * 条件：手牌中幺九牌的种类数 ≥ 9，且是自己第一巡（未鸣牌）
 */
export function canNineTerminals(state: GameState, seat: Seat): boolean {
  const player = state.players[seat];
  // 必须是第一巡（自己还没打过牌）
  if (player.discards.length > 0) return false;
  if (player.melds.length > 0) return false;

  const counts = handToCounts(player.hand);
  let kinds = 0;
  for (let i = 0; i < 34; i++) {
    if (counts[i] > 0 && isYaochu(i)) kinds++;
  }
  return kinds >= 9;
}

/**
 * 四风连打检查
 *
 * 条件：第一巡内，四家连续打出同一种风牌且无人鸣牌
 */
export function checkFourWinds(state: GameState): boolean {
  // 需要四家各打过 1 张，且都是同一种风牌
  const winds = [27, 28, 29, 30];
  for (const w of winds) {
    const allPlayedSameWind = state.players.every((p) => {
      if (p.discards.length !== 1) return false;
      const t = p.discards[0];
      const idx = t.suit === 'z' ? 27 + (Number(t.num) - 1) : -1;
      return idx === w;
    });
    if (allPlayedSameWind) {
      // 且无人鸣牌
      if (state.players.every((p) => p.melds.length === 0)) return true;
    }
  }
  return false;
}

/**
 * 四家立直检查
 */
export function checkFourRiichi(state: GameState): boolean {
  return state.players.every((p) => p.riichi);
}

/**
 * 三家和检查（一炮三响）
 */
export function checkTripleRon(candidates: readonly Seat[]): boolean {
  return candidates.length >= 3;
}

/**
 * 四杠散了检查
 *
 * 条件（天凤/雀魂一致）：场上杠子（暗杠/明杠/加杠）累计 ≥ 4，且**不属于同一家**。
 *   · 同一家 4 杠 = 四槓子（役满）→ 不算流局；若之后第 5 杠由他人开出 → 四杠散了。
 *   · 不同家凑满 4 个 → 立即流局。
 */
export function checkFourKans(state: GameState): boolean {
  let total = 0;
  const owners = new Set<Seat>();
  for (const p of state.players) {
    for (const m of p.melds) {
      if (m.kind === 'kantsu') {
        total++;
        owners.add(p.seat);
      }
    }
  }
  if (total >= 5) return true; // 第 5 个杠必然散
  return total >= 4 && owners.size >= 2; // 4 杠分属 ≥2 家
}

// ════════════════════════════════════════════════════════════════
// 听牌判定（流局罚符用）
// ════════════════════════════════════════════════════════════════

/**
 * 某家是否听牌（用于流局罚符）
 */
export function isTenpaiPlayer(state: GameState, seat: Seat): boolean {
  const player = state.players[seat];
  const counts = handToCounts(player.hand);
  return shanten(counts, player.melds.length) <= 0;
}

/** 全部听牌者 */
export function tenpaiSeats(state: GameState): Seat[] {
  return state.players.filter((p) => isTenpaiPlayer(state, p.seat)).map((p) => p.seat);
}

// ════════════════════════════════════════════════════════════════
// 流局罚符
// ════════════════════════════════════════════════════════════════

/**
 * 计算流局罚符
 *
 * 标准规则（天鳳/雀魂一致）——**总罚符恒定 3000 点**：
 *
 *   | 听牌 T | 未听牌 N | 每家未听牌者付 | 每家听牌者收 |
 *   |--------|----------|----------------|--------------|
 *   |   1    |    3     |   -1000        |   +3000      |
 *   |   2    |    2     |   -1500        |   +1500      |
 *   |   3    |    1     |   -3000        |   +1000      |
 *   |  0/4   |  4/0     |    无罚符      |    无罚符    |
 *
 * 统一公式：
 *   未听牌者支付总额 = 3000（恒定）
 *   每家未听牌者付  = 3000 / N
 *   每家听牌者收    = 3000 / T
 *
 * ⚠️ 常见错误：以为"每家未听牌者固定付 1000" —— 那是 T=1 的特例。
 *
 * @returns 各座位的点数变化（索引 = 座位）
 */
export function calculateDrawPenalty(tenpai: readonly Seat[]): number[] {
  const changes = [0, 0, 0, 0];
  const tenpaiCount = tenpai.length;
  const notTenpaiCount = 4 - tenpaiCount;

  if (tenpaiCount === 0 || tenpaiCount === 4) return changes;

  const TOTAL_PENALTY = 3000;
  const perNotTenpai = TOTAL_PENALTY / notTenpaiCount; // 每家未听牌者支付
  const perTenpai = TOTAL_PENALTY / tenpaiCount; // 每家听牌者收入

  for (let s = 0; s < 4; s++) {
    changes[s] = tenpai.includes(s as Seat) ? perTenpai : -perNotTenpai;
  }
  return changes;
}

// ════════════════════════════════════════════════════════════════
// 连庄 / 轮庄
// ════════════════════════════════════════════════════════════════

/**
 * 判断是否连庄
 *
 * 连庄条件：
 *   · 庄家和了
 *   · 庄家听牌流局
 */
export function shouldRepeatRound(state: GameState, result: RoundResult): boolean {
  const dealer = dealerSeat(state.round);

  if (result.kind === 'tsumo' || result.kind === 'ron') {
    return result.winner === dealer;
  }
  // 途中流局（九种九牌/四风连打/四家立直/四杠散了/三家和）→ 本场+1，亲家连庄
  //   与荒牌流局不同：不看听牌，一定连庄。
  if (result.reason !== 'exhaustive') return true;
  // 荒牌流局：庄家听牌则连庄
  return result.tenpaiSeats.includes(dealer);
}

/**
 * 计算下一局的信息
 *
 * @returns 下一局的 RoundInfo（含场风推进）
 */
export function nextRound(state: GameState, result: RoundResult): RoundInfo {
  const current = state.round;
  const repeat = shouldRepeatRound(state, result);

  // ⚠️ 供托（立直棒）的处理：
  //    · 和牌局 → 供托已归和牌者 → **清零**
  //    · 流局   → 供托保留到下一局
  //    （踩过：不清零导致下一局和牌时重复计算供托 → 点数不守恒）
  const carriedSticks = result.kind === 'draw' ? current.riichiSticks : 0;

  if (repeat) {
    // 连庄：本场数 +1
    return { ...current, honba: current.honba + 1, riichiSticks: carriedSticks };
  }

  // 轮庄：本场清零，局数 +1
  const nextNum = (current.roundNumber === 4 ? 1 : current.roundNumber + 1) as 1 | 2 | 3 | 4;
  let nextWind = current.roundWind;

  if (current.roundNumber === 4) {
    // 东风战 4 局结束 → 进入南场（或结束）
    const winds: Wind[] = ['east', 'south', 'west', 'north'];
    const idx = winds.indexOf(current.roundWind);
    nextWind = winds[Math.min(idx + 1, winds.length - 1)];
  }

  return {
    roundWind: nextWind,
    roundNumber: nextNum,
    honba: 0,
    riichiSticks: carriedSticks, // 和牌→0；流局→保留
  };
}

/**
 * 对局是否结束
 *
 * 东风战：东 1-4 局结束（或有人被击飞）
 * 半庄战：东 1-4 + 南 1-4
 */
export function isGameOver(
  round: RoundInfo,
  scores: readonly number[],
  gameLength: 'east' | 'hanchan' = 'east',
): boolean {
  // 击飞（某家 < 0 点）
  if (scores.some((s) => s < 0)) return true;

  if (gameLength === 'east') {
    // 东风战：进入南场（= 东 4 已结束）→ 对局结束
    return round.roundWind !== 'east';
  }
  // 半庄：进入西场（= 南 4 已结束）→ 对局结束
  return round.roundWind === 'west' || round.roundWind === 'north';
}

// ════════════════════════════════════════════════════════════════
// 结算
// ════════════════════════════════════════════════════════════════

/**
 * 应用点数变化到玩家
 */
export function applyScoreChanges(
  state: GameState,
  changes: readonly number[],
): GameState {
  return {
    ...state,
    players: state.players.map((p) => ({
      ...p,
      score: p.score + (changes[p.seat] ?? 0),
    })),
  };
}

/**
 * 结算和了（含供托 + 本场 bonus）
 *
 * @param baseChanges 基础点数变化（由 score 模块算出，不含供托/本场）
 * @param winner 和牌者
 * @param state 当前状态
 */
export function settleWin(
  state: GameState,
  baseChanges: readonly number[],
  winner: Seat,
): { changes: number[]; winnerGain: number } {
  const changes = [...baseChanges];
  // 供托（立直棒）归和牌者
  const sticks = state.round.riichiSticks * 1000;
  changes[winner] += sticks;

  // 本场 bonus（每本场 +300，由放铳者或三家平摊 —— 已在 score 模块处理）
  return { changes, winnerGain: changes[winner] };
}

/**
 * 结算流局
 */
export function settleDraw(
  state: GameState,
  reason: 'exhaustive' | 'nine_terminals' | 'four_winds' | 'four_riichi' | 'four_kans' | 'triple_ron',
): RoundResult {
  const tenpai = tenpaiSeats(state);
  const changes =
    reason === 'exhaustive'
      ? calculateDrawPenalty(tenpai)
      : [0, 0, 0, 0]; // 途中流局无罚符

  return {
    kind: 'draw',
    reason,
    tenpaiSeats: tenpai,
    scoreChanges: changes,
  };
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 流局原因中文名 */
export function drawReasonName(r: string): string {
  const names: Record<string, string> = {
    exhaustive: '荒牌流局',
    nine_terminals: '九种九牌',
    four_winds: '四风连打',
    four_riichi: '四家立直',
    four_kans: '四杠散了',
    triple_ron: '三家和',
  };
  return names[r] ?? r;
}

/** 顺位（按点数降序） */
export function ranking(scores: readonly number[]): number[] {
  const indexed = scores.map((s, i) => ({ s, i }));
  indexed.sort((a, b) => b.s - a.s);
  const result = new Array(4).fill(0);
  indexed.forEach((item, rank) => {
    result[item.i] = rank + 1;
  });
  return result;
}

/** 格式化顺位（如 "1位 32000"） */
export function formatRanking(scores: readonly number[]): string {
  const ranks = ranking(scores);
  return scores.map((s, i) => `${ranks[i]}位 ${s}`).join(' | ');
}
